import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import { UnassignedItem } from 'src/entities/dispatch';
import { PlaceSide } from 'src/pages/ConvenienceDispatches/placeKeys';

export type UnassignedTooltipRow = { label: string; value: string };
export type UnassignedTooltipVM = { title: string; rows: UnassignedTooltipRow[] };

/**
 * ピンにぶら下がる 1 件。
 *
 * ピンは積地にも立つので、案件だけでは表示内容が決まらない
 * (logposeTech/log_web_issues#319)。同じ案件でも積地ピンと降地ピンで出す地点名・
 * 指定時間が違うため、「どちら側としてこのピンにぶら下がっているか」を一緒に持つ。
 */
export type UnassignedStop = { item: UnassignedItem; side: PlaceSide };

/** その側の地点名。積地ピンで降地名を出すと、ピンの立っている場所と違う地点を指す */
const placeNameOf = ({ item, side }: UnassignedStop): string =>
  (side === 'loading' ? item.origin : item.storeName) || '—';

/**
 * 指定時間の解決。指定が無くても時間枠は運行の目安になるので順に落とす。
 *
 * **その側の指定時間を優先する**。積地ピンに降地の指定時間を出すと、別地点の約束時刻を
 * その場所の時刻として読ませてしまう（実データに積地 03:00-16:00 / 降地 08:00-11:00 の
 * 案件がある。logposeTech/log_web_issues#319）。
 */
const timeWindowOf = ({ item, side }: UnassignedStop): string => {
  if (side === 'loading') {
    return item.loadingDesignatedTimeWindow || item.originTimeWindow || item.destTimeWindow || '指定なし';
  }
  return item.designatedTimeWindow || item.destTimeWindow || item.originTimeWindow || '指定なし';
};

/** 伝票の枚数。枚数が無ければ伝票一覧の数で数える */
const invoiceCountOf = (item: UnassignedItem): number =>
  item.invoiceCount ?? (item.invoices?.length ?? 0);

/**
 * 未割当ピンのホバーに出す内容。
 *
 * サンプル (route-planner components/dispatch/MapView.tsx:186-224) と同じ 6 項目。
 * 詳細カードはクリックで出す（ホバーで出すと、地図を横切るだけで開閉して読めない）。
 */
export function buildUnassignedTooltip(stop: UnassignedStop): UnassignedTooltipVM {
  const { item } = stop;
  // 未設定を空欄にすると「取れていない」のか「0」なのか読めない
  const cases = item.cases ?? 0;
  const weight = item.weight ?? 0;
  const volume = item.volume ?? 0;
  const invoiceCount = invoiceCountOf(item);
  const time = timeWindowOf(stop);
  const shippers = item.shippers ?? [];

  return {
    title: placeNameOf(stop),
    rows: [
      // 丸め方と桁区切りは他の表示と共通（metricText）。生の値を出すと
      // 浮動小数の足し算の端数がそのまま見える
      { label: '数量', value: `${quantityValueText(cases)} cs` },
      { label: '重量', value: `${weightValueText(weight)} kg` },
      { label: '体積', value: `${volumeValueText(volume)} m³` },
      { label: '伝票', value: `${invoiceCount} 枚` },
      { label: '指定時間', value: time },
      { label: '荷主', value: shippers.length > 0 ? shippers.join(', ') : '—' },
    ],
  };
}

/**
 * 1 本のピンにぶら下がる未割当案件のホバー内容。
 *
 * 地点ごとに 1 ピンへまとめたので（logposeTech/log_web_issues#2430）、ホバーには
 * 複数案件が乗りうる。1 案件ぶんの 6 行 × N 件を並べると画面外まで伸びるため、
 * 合算したサマリを 1 つだけ出す。行の並びは単独ピンと揃える（件数で行が増減すると
 * 読み手が場所を覚え直すことになる）。
 */
export function buildUnassignedPlaceTooltip(stops: readonly UnassignedStop[]): UnassignedTooltipVM {
  // 1 件のときは単独ピンとまったく同じ内容にする（見え方を分岐させない）
  if (stops.length === 1) return buildUnassignedTooltip(stops[0]);

  // 「積んだ場所で降ろす案件」は同じ地点に積地と降地の 2 つの stop としてぶら下がる
  // （実データにある）。名称と指定時間は**両方の side を見る**（片側を捨てると、どちらが
  // 残るかが表示フィルタ次第で変わる）が、数量と件数は**案件ごとに 1 回だけ**数える。
  // 同じ案件を 2 回足すと数量が倍になる
  const uniqueItems = [...new Map(stops.map(({ item }) => [item.id, item])).values()];

  const totals = uniqueItems.reduce(
    (acc, item) => ({
      cases: acc.cases + (item.cases ?? 0),
      weight: acc.weight + (item.weight ?? 0),
      volume: acc.volume + (item.volume ?? 0),
      invoiceCount: acc.invoiceCount + invoiceCountOf(item),
    }),
    { cases: 0, weight: 0, volume: 0, invoiceCount: 0 },
  );

  // 割れているのに 1 つだけ出すと、その時間で全件回れると読めてしまう
  const timeWindows = new Set(stops.map(timeWindowOf));
  const time = timeWindows.size === 1 ? [...timeWindows][0] : '複数';
  const shippers = [...new Set(uniqueItems.flatMap((item) => item.shippers ?? []))];

  // 同じ座標でも名称が揃わないことがある（取り込み元の表記ゆれ、同一座標の別テナント。
  // 実データに「積地としても降地としても使われ、名称が複数ある」地点がある）。
  // 先頭だけ出すと残りも同じ名前だと読めてしまうので、複数あることを見出しに示す
  const names = [...new Set(stops.map(placeNameOf))];
  const heading = names.length === 1 ? names[0] : `${names[0]} ほか`;

  return {
    title: `${heading}（${uniqueItems.length} 件）`,
    rows: [
      { label: '数量', value: `${quantityValueText(totals.cases)} cs` },
      { label: '重量', value: `${weightValueText(totals.weight)} kg` },
      { label: '体積', value: `${volumeValueText(totals.volume)} m³` },
      { label: '伝票', value: `${totals.invoiceCount} 枚` },
      { label: '指定時間', value: time },
      { label: '荷主', value: shippers.length > 0 ? shippers.join(', ') : '—' },
    ],
  };
}

/**
 * マーカーは地点の id しか持たないので、その id から引ける形にして渡す。
 *
 * 表示内容はマーカーの `entries`（案件 id と、その案件がこの地点を積地/降地の
 * どちらとして使うか）と案件データから組む。
 *
 * Why not マーカー側に地点名を持たせる: 楽観削除でその名称の由来になった案件だけが
 * 消えても、見出しに残り続ける（ピンは残った案件のために立っているのに、消えた案件の
 * 名前を名乗る）。名前も時刻も案件データ側から引けば、絞り込みに自然に追従する。
 *
 * item が引けない id（楽観削除の途中など）は数えない。引けたぶんだけで組む。
 */
export function buildUnassignedTooltipMap(
  markers: readonly { id: string; entries: readonly { id: string; side: PlaceSide }[] }[],
  items: readonly UnassignedItem[],
): Map<string, UnassignedTooltipVM> {
  const itemById = new Map(items.map((item) => [item.id, item]));
  const result = new Map<string, UnassignedTooltipVM>();
  markers.forEach((marker) => {
    const stops = marker.entries
      .map(({ id, side }) => {
        const item = itemById.get(id);
        return item ? { item, side } : null;
      })
      .filter((stop): stop is UnassignedStop => stop != null);
    if (stops.length === 0) return;
    result.set(marker.id, buildUnassignedPlaceTooltip(stops));
  });
  return result;
}
